// store/AuthStore.js
import { defineStore } from 'pinia';
import { jwtDecode } from 'jwt-decode';
import { useSettingStore } from "@/store/SettingsStore";

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const useAuthStore = defineStore({
    id: 'auth', // Unique store ID
    state: () => ({
        user_token: '', // Only store the token
        user_loggedIn: false,
        token_timestamp: '', // Store timestmap of token to refresh
        user: {},
        refreshToken: '',
    }),
    actions: {
        login(token, refreshToken) {
            console.log('logged in');
            this.user_token = token;
            this.refreshToken = refreshToken;
            this.user_loggedIn = true;
            this.getUserInfo(); // Ignore promise as we don't need to wait for it
            const settingStore = useSettingStore();
            settingStore.fetchUserSettings();
        },
        async getUserInfo() {
            if (!this.user_token) return;
            try {
                const response = await fetch(API_URL + '/v1/users/me', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${this.user_token}`,
                        'Content-Type': 'application/json',
                    },
                    redirect: 'error', // Fail on redirects instead of following
                });
                if (response.ok) {
                    const data = await response.json();
                    // Handle wrapped response format
                    this.user = data?.data || data;
                }
            } catch (error) {
                console.log("Error fetching user info:", error.message);
            }
        },
        async refreshAccessToken() {
            console.log('[AuthStore] Refreshing access token...');
            try {
                const response = await fetch(API_URL + '/auth/refresh', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 'refresh_token': this.refreshToken }),
                });

                if (response.ok) {
                    const data = await response.json();
                    this.user_token = data.token;
                    this.refreshToken = data.refresh_token;
                    this.token_timestamp = jwtDecode(data.token).exp;
                    console.log("[AuthStore] Token refreshed successfully");
                    return data;
                } else {
                    const errorText = await response.text();
                    console.error('[AuthStore] Refresh failed:', response.status, errorText);
                    throw new Error(`Refresh failed: ${response.status}`);
                }
            } catch (error) {
                console.error("[AuthStore] Error refreshing token:", error);
                throw error;
            }
        },
        async logout() {
            try {
                this.user_token = '';
                this.user_loggedIn = false;
                this.refreshToken = '';
                this.user = {};
                this.token_timestamp = '';
                console.log('User logged out');
            } catch (error) {
                console.log("Error logging out: ", error);
            }
        },
    },
    getters: {
        isAuthenticated: (state) => !!state.user_token,
        getUserToken: (state) => state.user_token,
        getUser: (state) => state.user,
        getRefreshToken: (state) => state.refreshToken,
        isAdmin: (state) => {
            if (state.user.roles) {
                return state.user.roles.includes('ROLE_ADMIN'); // As per API roles
            }
        }
    },
    persist: true, // Persist this to LS
    subscribe: (mutation) => {
        if (mutation.store.user_loggedIn === false) {
            mutation.store.user_token = '';
            mutation.store.refreshToken = '';
            mutation.store.token_timestamp = '';
        }
    },
});

